// Metafield schema
const PRODUCT_METAFIELDS = `
  metafields(
    identifiers: [
      {namespace: "custom", key: "badges"},
    ]
  ) {
    reference {
      ... on MediaImage {
        image {
          originalSrc
        }
      }
    }
    value
    key
    namespace
  }
`

const COLLECTION_METAFIELDS = `
  metafields(
    identifiers: [
      {namespace: "style_group", key: "is_style_group"},
      {namespace: "style_group", key: "main_image"},
      {namespace: "custom", key: "associated_products"},
      {namespace: "custom", key: "size_chart_override"}
    ]
  ) {
    reference {
      ... on MediaImage {
        image {
          originalSrc
        }
      }
      ... on GenericFile {
        url
      }
    }
    value
    key
    namespace
  }
`

const VARIANT_METAFIELDS = `
  metafields(
    identifiers: [
      {namespace: "backorder", key: "ship_date"},
      {namespace: "custom", key: "style_number"},
    ]
  ) {
    reference {
      ... on MediaImage {
        image {
          originalSrc
        }
      }
    }
    value
    key
    namespace
  }
`

// Object schemas
const VARIANT_FIELDS = `
  id
  title
  priceV2 {
    amount
    currencyCode
  }
  compareAtPriceV2 {
    amount
    currencyCode
  }
  image {
    url
    altText
  }
  weight
  availableForSale
  quantityAvailable
  sku
  selectedOptions {
    name
    value
  }
  currentlyNotInStock
  quantityAvailable
  ${VARIANT_METAFIELDS}
`

const PRODUCT_FIELDS = `
  availableForSale
  id
  title
  descriptionHtml
  handle
  priceRange {
    minVariantPrice {
      amount
    }
    maxVariantPrice {
      amount
    }
  }
  productType
  vendor
  createdAt
  updatedAt
  publishedAt
  onlineStoreUrl
  tags
  images(first: 10) {
    edges {
      node {
        src
        altText
      }
    }
  }
  options {
    id
    name
    values
  }
  ${PRODUCT_METAFIELDS}
`

const COLLECTION_FIELDS = `
  id
  title
  handle
  description
  updatedAt
  image {
    src
    altText
  }
  ${COLLECTION_METAFIELDS}
`

// Queries
export const GetProductById = `
  query GetProductById($id: ID!) {
    node(id: $id) {
      ... on Product {
        ${PRODUCT_FIELDS}
        variants(first: 250) {
          edges {
            node {
              ${VARIANT_FIELDS}
            }
          }
        }
        collections(first: 250) {
          edges {
            node {
              ${COLLECTION_FIELDS}
            }
          }
        }
      }
    }
  }
`

export const GetProductsByBatch = `
  query GetProductsByBatch($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        ${PRODUCT_FIELDS}
        variants(first: 250) {
          edges {
            node {
              ${VARIANT_FIELDS}
            }
          }
        }
      }
    }
  }
`

export const GetProductsByCollectionId = `
  query GetProductsByCollectionId($id: ID!, $limit: Int = 250, $afterCursor: String) {
    node(id: $id) {
      ... on Collection {
        products(first: $limit, after: $afterCursor) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            cursor
            node {
              id
              title
              priceRange {
                minVariantPrice {
                  amount
                }
                maxVariantPrice {
                  amount
                }
              }
              productType
              vendor
              createdAt
              updatedAt
              publishedAt
              tags
              options {
                id
                name
                values
              }
              metafields(
                identifiers: [
                  {namespace: "style_group", key: "collection"},
                  {namespace: "swatch", key: "image"}
                ]
              ) {
                reference {
                  ... on MediaImage {
                    image {
                      originalSrc
                    }
                  }
                }
                value
                key
                namespace
              }
            }
          }
        }
      }
    }
  }
`

export const GetCollectionById = `
  query GetCollectionById($id: ID!, $limit: Int = 250, $afterCursor: String) {
    node(id: $id) {
      ... on Collection {
        ${COLLECTION_FIELDS}
        products(first: $limit, after: $afterCursor) {
          pageInfo {
            hasNextPage
            hasPreviousPage
          }
          edges {
            cursor
            node {
              ${PRODUCT_FIELDS}
              variants(first: 250) {
                edges {
                  node {
                    ${VARIANT_FIELDS}
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

export const GetCollectionsByBatch = `
  query GetCollectionsByBatch($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Collection {
        ${COLLECTION_FIELDS}
        products(first: 250) {
          pageInfo {
            hasNextPage
            hasPreviousPage
          }
          edges {
            cursor
            node {
              ${PRODUCT_FIELDS}
              variants(first: 250) {
                edges {
                  node {
                    ${VARIANT_FIELDS}
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

export const GetCollectionsByTitles = `
  query GetCollectionsByTitles($titlesQuery: String!) {
    collections(first: 10, query: $titlesQuery) {
      edges {
        node {
          ${COLLECTION_FIELDS}
          products(first: 250) {
            pageInfo {
              hasNextPage
              hasPreviousPage
            }
            edges {
              cursor
              node {
                ${PRODUCT_FIELDS}
                variants(first: 250) {
                  edges {
                    node {
                      ${VARIANT_FIELDS}
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

export const SearchProducts = `
  query SearchProducts($query: String!, $limit: Int = 250, $afterCursor: String) {
    products(first: $limit, after: $afterCursor, query: $query) {
      edges {
        node {
          ${PRODUCT_FIELDS}
          variants(first: 250) {
            edges {
              node {
                ${VARIANT_FIELDS}
              }
            }
          }
        }
      }
    }
  }
`

export const GetVariantById = `
  query GetVariantById($id: ID!) {
    node(id: $id) {
      ... on ProductVariant {
        ${VARIANT_FIELDS}
      }
    }
  }
`
